import { ReactNode, useState } from 'react';
import { useRouter } from 'next/router';

import dayjs from 'dayjs';

// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Utils
import {
    getLocalStorageNewFeatureNotification,
    setLocalStorageNewFeatureNotification
} from '@/utils/localStorage/newFeatureNotificationLocalStorage';

// Lib
import { LinkHrefType } from '@/lib/menu/main';
// Types
import type { NewFeatureReleasedType } from '@/lib/releases/newFeaturesReleased';
import NEW_FEATURES_RELEASED from '@/lib/releases/newFeaturesReleased';

// Components
import { GLOBAL_EVENT_QUERY } from '@/components/composite/Listeners/GlobalEventsListener';

const NOTIFICATION_DURATION = 60;

export const useNewFeatureNotifications = () => {
    const { activeSocialProfile } = useActiveSocialProfile();

    const [newFeaturesVisited, setNewFeaturesVisited] = useState(
        getLocalStorageNewFeatureNotification()
    );

    const markNewFeatureAsVisited = (feature: NewFeatureReleasedType['name']) => {
        setLocalStorageNewFeatureNotification([...newFeaturesVisited, feature]);
        setNewFeaturesVisited((prevState: NewFeatureReleasedType['name'][]) => [
            ...prevState,
            feature
        ]);
    };

    const getMatchedFeatureForUrls = (
        urls: LinkHrefType[],
        parentOnly?: boolean
    ): NewFeatureReleasedType | null => {
        let matchedFeature: NewFeatureReleasedType | null = null;
        if (!urls) return null;
        urls.forEach((url) => {
            NEW_FEATURES_RELEASED.forEach((feature) => {
                if (
                    feature?.name &&
                    (parentOnly === undefined || Boolean(feature?.parentMenu) === parentOnly) &&
                    !newFeaturesVisited.includes(feature?.name) &&
                    ((!feature?.urls && !feature?.globalEventQuery) ||
                        (typeof url !== 'object' && feature?.urls?.includes(url)) ||
                        (typeof url === 'object' &&
                            feature?.globalEventQuery &&
                            url?.query?.[GLOBAL_EVENT_QUERY] === feature?.globalEventQuery)) &&
                    (!feature?.socialNetworkType ||
                        feature?.socialNetworkType.includes(
                            activeSocialProfile?.social_network_type
                        ))
                ) {
                    if (feature?.date) {
                        const dateDiff = dayjs().diff(
                            dayjs(feature?.date).format('YYYY-MM-DD'),
                            'day'
                        );
                        if (dateDiff >= 0 && dateDiff <= NOTIFICATION_DURATION) {
                            matchedFeature = feature;
                        }
                    } else {
                        matchedFeature = feature;
                    }
                }
            });
        });
        return matchedFeature;
    };

    // For submenu items — skips parentMenu:true features
    const isNewFeatureOnPages = (urls: LinkHrefType[]) =>
        getMatchedFeatureForUrls(urls, false)?.name ?? null;

    const getNewFeatureTooltipForUrls = (urls: LinkHrefType[]): string | undefined =>
        getMatchedFeatureForUrls(urls, false)?.tooltip;

    // For parent menu entries — only checks parentMenu:true features
    const isNewParentFeatureOnPages = (urls: LinkHrefType[]) =>
        getMatchedFeatureForUrls(urls, true)?.name ?? null;

    return {
        newFeaturesVisited,
        markNewFeatureAsVisited,
        isNewFeatureOnPages,
        getNewFeatureTooltipForUrls,
        isNewParentFeatureOnPages
    };
};

const PageNewFeatureVisitedHandler = ({ children }: { children: ReactNode }) => {
    const { pathname } = useRouter();
    const urlWithoutParams = pathname.split('?')[0];

    const { markNewFeatureAsVisited, isNewFeatureOnPages, isNewParentFeatureOnPages } =
        useNewFeatureNotifications();

    const currentNewFeature = isNewFeatureOnPages([urlWithoutParams]);
    const currentNewParentFeature = isNewParentFeatureOnPages([urlWithoutParams]);

    if (currentNewFeature) {
        markNewFeatureAsVisited(currentNewFeature);
    }
    if (currentNewParentFeature) {
        markNewFeatureAsVisited(currentNewParentFeature);
    }

    return children;
};

export default PageNewFeatureVisitedHandler;
